import {
  type AppBskyFeedDefs,
  type AppBskyFeedGetFeed as GetCustomFeed,
  BskyAgent,
  jsonStringToLex,
} from '@atproto/api'

import {DISCOVER_FEED_URI} from '#/lib/constants'
import {fetchVerifiedUsersList} from '#/lib/api/verified'
import {
  getAppLanguageAsContentLanguage,
  getContentLanguages,
} from '#/state/preferences/languages'
import {type FeedAPI, type FeedAPIResponse} from './types'
import {createBskyTopicsHeader, isBlueskyOwnedFeed} from './utils'

const DISCOVER_CURSOR_PREFIX = 'discover:'
const PRIORITY_AUTHOR_INJECT_COUNT = 5
const VERIFIED_USERS_CACHE_MS = 5 * 60 * 1000 // 5 min

let verifiedDidsCache: {dids: string[]; fetchedAt: number} | null = null

async function getVerifiedUserDids(): Promise<string[]> {
  if (
    verifiedDidsCache &&
    Date.now() - verifiedDidsCache.fetchedAt < VERIFIED_USERS_CACHE_MS
  ) {
    return verifiedDidsCache.dids
  }
  const dids = await fetchVerifiedUsersList()
  verifiedDidsCache = {dids, fetchedAt: Date.now()}
  return dids
}

export class CustomFeedAPI implements FeedAPI {
  agent: BskyAgent
  params: GetCustomFeed.QueryParams
  userInterests?: string

  constructor({
    agent,
    feedParams,
    userInterests,
  }: {
    agent: BskyAgent
    feedParams: GetCustomFeed.QueryParams
    userInterests?: string
  }) {
    this.agent = agent
    this.params = feedParams
    this.userInterests = userInterests
  }

  async peekLatest(): Promise<AppBskyFeedDefs.FeedViewPost> {
    const contentLangs = getContentLanguages().join(',')
    const res = await this.agent.app.bsky.feed.getFeed(
      {
        ...this.params,
        limit: 1,
      },
      {headers: {'Accept-Language': contentLangs}},
    )
    return res.data.feed[0]
  }

  async fetch({
    cursor,
    limit,
  }: {
    cursor: string | undefined
    limit: number
  }): Promise<FeedAPIResponse> {
    const contentLangs = getContentLanguages().join(',')
    const agent = this.agent
    const isBlueskyOwned = isBlueskyOwnedFeed(this.params.feed)

    const res = agent.did
      ? await this.agent.app.bsky.feed.getFeed(
          {
            ...this.params,
            cursor,
            limit,
          },
          {
            headers: {
              ...(isBlueskyOwned
                ? createBskyTopicsHeader(this.userInterests)
                : {}),
              'Accept-Language': contentLangs,
            },
          },
        )
      : await loggedOutFetch({...this.params, cursor, limit})
    if (res.success) {
      // NOTE
      // some custom feeds fail to enforce the pagination limit
      // so we manually truncate here
      // -prf
      if (res.data.feed.length > limit) {
        res.data.feed = res.data.feed.slice(0, limit)
      }
      return {
        cursor: res.data.feed.length ? res.data.cursor : undefined,
        feed: res.data.feed,
      }
    }
    return {
      feed: [],
    }
  }
}

/**
 * Discover (whats-hot) feed with priority author posts prepended on first page
 * so they appear in the Discover tab.
 */
export class DiscoverWithPriorityAuthorAPI implements FeedAPI {
  agent: BskyAgent
  userInterests?: string
  delegate: CustomFeedAPI

  constructor({
    agent,
    userInterests,
  }: {
    agent: BskyAgent
    userInterests?: string
  }) {
    this.agent = agent
    this.userInterests = userInterests
    this.delegate = new CustomFeedAPI({
      agent,
      feedParams: {feed: DISCOVER_FEED_URI},
      userInterests,
    })
  }

  async peekLatest(): Promise<AppBskyFeedDefs.FeedViewPost> {
    const dids = await getVerifiedUserDids()
    for (const did of dids) {
      try {
        const authorRes = await this.agent.getAuthorFeed({
          actor: did,
          limit: 1,
          filter: 'posts_no_replies',
        })
        if (authorRes.data.feed?.length) {
          return authorRes.data.feed[0]
        }
      } catch {
        // skip failed lookups
      }
    }
    return this.delegate.peekLatest()
  }

  async fetch({
    cursor,
    limit,
  }: {
    cursor: string | undefined
    limit: number
  }): Promise<FeedAPIResponse> {
    const isFirstPage = !cursor || !cursor.startsWith(DISCOVER_CURSOR_PREFIX)

    if (isFirstPage) {
      const discoverCursor = cursor === DISCOVER_CURSOR_PREFIX ? undefined : cursor
      let authorPosts: AppBskyFeedDefs.FeedViewPost[] = []
      const seenUris = new Set<string>()
      try {
        const dids = await getVerifiedUserDids()
        const postsPerUser = 2
        const maxUsers = Math.min(dids.length, 10)
        for (let i = 0; i < maxUsers && authorPosts.length < PRIORITY_AUTHOR_INJECT_COUNT * 2; i++) {
          const did = dids[i]
          try {
            const authorRes = await this.agent.getAuthorFeed({
              actor: did,
              limit: postsPerUser,
              filter: 'posts_no_replies',
            })
            const posts = authorRes.data.feed ?? []
            for (const p of posts) {
              const uri = p.post?.uri
              if (uri && !seenUris.has(uri)) {
                seenUris.add(uri)
                authorPosts.push(p)
              }
            }
          } catch {
            // skip failed lookups
          }
        }
      } catch {
        // Fall back to discover-only if verified list fails (e.g. network)
      }
      const discoverLimit = Math.max(1, limit - authorPosts.length)
      const discoverRes = await this.delegate.fetch({
        cursor: discoverCursor,
        limit: discoverLimit,
      })
      const feed = [...authorPosts, ...discoverRes.feed]
      const nextCursor = discoverRes.cursor
        ? `${DISCOVER_CURSOR_PREFIX}${discoverRes.cursor}`
        : undefined
      return {feed, cursor: nextCursor}
    }

    const discoverCursor = cursor.slice(DISCOVER_CURSOR_PREFIX.length)
    return this.delegate.fetch({cursor: discoverCursor, limit})
  }
}

// HACK
// we want feeds to give language-specific results immediately when a
// logged-out user changes their language. this comes with two problems:
// 1. not all languages have content, and
// 2. our public caching layer isnt correctly busting against the accept-language header
// for now we handle both of these with a manual workaround
// -prf
async function loggedOutFetch({
  feed,
  limit,
  cursor,
}: {
  feed: string
  limit: number
  cursor?: string
}) {
  let contentLangs = getAppLanguageAsContentLanguage()

  /**
   * Copied from our root `Agent` class
   * @see https://github.com/bluesky-social/atproto/blob/60df3fc652b00cdff71dd9235d98a7a4bb828f05/packages/api/src/agent.ts#L120
   */
  const labelersHeader = {
    'atproto-accept-labelers': BskyAgent.appLabelers
      .map(l => `${l};redact`)
      .join(', '),
  }

  // manually construct fetch call so we can add the `lang` cache-busting param
  let res = await fetch(
    `https://api.bsky.app/xrpc/app.bsky.feed.getFeed?feed=${feed}${
      cursor ? `&cursor=${cursor}` : ''
    }&limit=${limit}&lang=${contentLangs}`,
    {
      method: 'GET',
      headers: {'Accept-Language': contentLangs, ...labelersHeader},
    },
  )
  let data = res.ok
    ? (jsonStringToLex(await res.text()) as GetCustomFeed.OutputSchema)
    : null
  if (data?.feed?.length) {
    return {
      success: true,
      data,
    }
  }

  // no data, try again with language headers removed
  res = await fetch(
    `https://api.bsky.app/xrpc/app.bsky.feed.getFeed?feed=${feed}${
      cursor ? `&cursor=${cursor}` : ''
    }&limit=${limit}`,
    {method: 'GET', headers: {'Accept-Language': '', ...labelersHeader}},
  )
  data = res.ok
    ? (jsonStringToLex(await res.text()) as GetCustomFeed.OutputSchema)
    : null
  if (data?.feed?.length) {
    return {
      success: true,
      data,
    }
  }

  return {
    success: false,
    data: {feed: []},
  }
}
